import { apiRequest } from "@/api/ApiRequest";
import type {
  CmmnCodeOptionType,
  CmmnCodeType,
  DeptType,
  StaffType,
  StudentType,
  CudResultType,
} from "@/types/cmmn/CmmnTypes";
import { ApiMethod } from "@/types/Enums";
import type { ExcelHeader } from "@/types/ExcelTypes";

const _url = "/api/v1/cmmn";
const VITE_APP_TYPE = import.meta.env.VITE_APP_TYPE as string;

/**
 * summary: Get Code List
 * #GET./api/v1/cmmn/code
 */
export const getCmmnList = async (params: ParamsType): Promise<CmmnCodeType[]> => {
  if (!params.upCd) {
    console.warn("upCd is null");
    throw new Error("upCd is required");
  }
  return await apiRequest<CmmnCodeType[]>({
    url: _url + "/code",
    params: params,
  });
};

/**
 * summary: Get Cmmn Code Option
 * #GET./api/v1/cmmn/code-option
 */
export const getCmmnCdOption = async (
  params: ParamsType
): Promise<Record<string, CmmnCodeOptionType[]>> => {
  if (!params.upCdArr) {
    console.warn("upCdArr is null");
    throw new Error("upCdArr is required");
  }

  const upCds = params.upCdArr as string[];
  if (upCds.length === 0) {
    console.warn("upCdArr length is 0");
    throw new Error("upCdArr length is 0");
  }
  return await apiRequest<Record<string, CmmnCodeOptionType[]>>({
    url: _url + "/code-option",
    params: params,
  });
};

/**
 * summary: Check Duplicate Value
 * #GET./api/v1/cmmn/dup-value
 */
export const chkDupValue = async (params: ParamsType): Promise<string[]> => {
  return await apiRequest<string[]>({
    method: ApiMethod.GET,
    url: _url + "/dup-value",
    params: params,
  });
};

export const uploadExcel = async (
  tableName: string,
  data: { headers: ExcelHeader[]; data: ParamsType[] }
): Promise<CudResultType> => {
  if (!tableName) {
    console.warn("tableName is null");
    throw new Error("tableName is required");
  }
  if (data.data.length === 0) {
    console.warn("data is empty");
    throw new Error("data is required");
  }
  if (data.headers.length === 0) {
    console.warn("headers is empty");
    throw new Error("headers is required");
  }
  return await apiRequest<CudResultType>({
    method: ApiMethod.POST,
    url: "/api/v1/cmmn/excel-upload",
    headers: {
      "Content-Type": "application/json",
    },
    data: {
      data: data.data,
      headers: data.headers,
      tableName: tableName,
    },
    useLoading: false,
  });
};

/**
 * summary: Get Student List
 * #GET./api/v1/auth/students
 */
export const getStudentList = async (params: ParamsType): Promise<StudentType[]> => {
  return await apiRequest<StudentType[]>({
    url: `/api/v1/auth/students`,
    params: params,
  });
};

/**
 * summary: Get Staff List
 * #GET./api/v1/auth/staffs
 */
export const getStaffList = async (params: ParamsType): Promise<StaffType[]> => {
  return await apiRequest<StaffType[]>({
    url: `/api/v1/auth/staffs`,
    params: params,
  });
};

/**
 * summary: Get All Student List(재학생+SNS)
 * #GET./api/v1/auth/students-all
 */
export const getAllStudentList = async (params: ParamsType): Promise<StudentType[]> => {
  return await apiRequest<StudentType[]>({
    url: `/api/v1/auth/students-all`,
    params: params,
  });
};

/**
 * summary: Get All Staff List(교직원+외부강사/외부상담사)
 * #GET./api/v1/auth/staffs-all
 */
export const getAllStaffList = async (params: ParamsType): Promise<StaffType[]> => {
  return await apiRequest<StaffType[]>({
    url: `/api/v1/auth/staffs-all`,
    params: params,
  });
};

/**
 * summary: Get Dept List
 * #GET./api/v1/cmmn/depts
 * @param params.deptDiv: edu: 교육조직 | adm: 행정조직
 * @param params.deptLevel: 공통으로 같은 레벨 조회 시 사용
 * @param params.eduLevel: 교육조직 레벨 조회 시 사용
 * @param params.admLevel: 행정조직 레벨 조회 시 사용
 * ----------- edu ----------
 * 1: 학과의 상위 그룹(COLEAGE)
 * 2: 학과(HAKGUA)
 * 3: 전공(MAJOR)
 * --------------------------
 * ----------- adm ----------
 * 1: 상위 그룹(UP_DEPT)
 * 2: 부서,과(DEPT)
 * --------------------------
 */
export const getDeptList = async (params: ParamsType): Promise<DeptType[]> => {
  return await apiRequest<DeptType[]>({
    url: `/api/v1/cmmn/depts`,
    params: params,
  });
};

/**
 * summary: Get Cur Year/Semester
 * #GET./api/v1/cmmn/current-year-semester
 */
export const getCurYearSemester = async (): Promise<{ year: string; semesterCd: string }> => {
  return await apiRequest<{ year: string; semesterCd: string }>({
    url: `/api/v1/cmmn/current-year-semester`,
  });
};

/**
 * summary: Get Check Dup User Id
 * #GET./api/v1/cmmn/dup-id
 */
export const getCheckDupUserId = async (userId: string): Promise<boolean> => {
  return await apiRequest<boolean>({
    url: `/api/v1/cmmn/external-user/dup`,
    params: { userId },
  });
};

export default {
  getCmmnList,
  getCmmnCdOption,
  getStudentList,
  getStaffList,
  getCurYearSemester,
  getCheckDupUserId,
};
